import React, { useState } from 'react';
import { useAuth } from '../AuthContext';

const GradeCollectionModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    grade: '',
    parentEmail: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { updateUserProfile, userProfile, user } = useAuth();

  // Grade options from Year 3 to Year 12
  const gradeOptions = [
    { value: '', label: 'Select Your Grade' },
    { value: '3', label: 'Year 3' },
    { value: '4', label: 'Year 4' },
    { value: '5', label: 'Year 5' },
    { value: '6', label: 'Year 6' },
    { value: '7', label: 'Year 7' },
    { value: '8', label: 'Year 8' },
    { value: '9', label: 'Year 9' },
    { value: '10', label: 'Year 10' },
    { value: '11', label: 'Year 11' },
    { value: '12', label: 'Year 12' }
  ];

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = () => {
    if (!formData.grade) {
      setError('Please select your grade');
      return false;
    }

    // Validate parent email if provided
    if (formData.parentEmail && formData.parentEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.parentEmail.trim())) {
        setError('Please enter a valid parent email address');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      // Update user profile with grade and parent email
      const updateData = {
        user_id: user?.id, // Use the auth user ID directly
        email: user?.email,
        full_name: userProfile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')[0],
        grade: formData.grade,
        parent_email: formData.parentEmail.trim() || null
      };

      console.log('🔍 Grade Collection Modal - Updating profile with data:', updateData);
      const result = await updateUserProfile(updateData);
      console.log('🔍 Grade Collection Modal - Update result:', result);
      
      if (result.success) {
        console.log('✅ Grade update successful, closing modal');
        onClose(); // Close modal on success
      } else {
        console.error('❌ Grade update failed:', result.error);
        setError(result.error || 'Failed to update profile');
      }
    } catch (error) {
      console.error('❌ Grade update exception:', error);
      setError('An unexpected error occurred: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Just one more step!</h2>
          <p className="text-gray-600">To personalize your learning experience, please tell us your current school year.</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="grade" className="block text-sm font-medium text-gray-700 mb-2">
              Select Your Grade *
            </label>
            <select
              id="grade"
              name="grade"
              value={formData.grade}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              {gradeOptions.map((option) => (
                <option key={option.value} value={option.value} disabled={!option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Only show parent email field if not already set */}
          {!userProfile?.parent_email && (
            <div>
              <label htmlFor="parentEmail" className="block text-sm font-medium text-gray-700 mb-2">
                Parent/Guardian Email (Optional)
              </label>
              <input
                id="parentEmail"
                name="parentEmail"
                type="email"
                value={formData.parentEmail}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="parent@example.com"
              />
            </div>
          )}

          {/* Privacy Message */}
          <div className="text-xs text-gray-500 text-center px-4 py-2 bg-gray-50 rounded-lg">
            We do not share your information with third parties. Parent emails are only used to send progress reports.
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-colors ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {loading ? 'Updating Profile...' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default GradeCollectionModal;